type PromiseItem<R> = {
  promiseFunction: () => Promise<R>;
  resolve: (value: R) => void;
  reject: (reason?: unknown) => void;
};

export class PromiseQueue<R> {
  private readonly maxConcurrent: number;
  private readonly currentRunning: Set<PromiseItem<R>>;
  private readonly queue: PromiseItem<R>[];
  private promisesTimeoutId?: ReturnType<typeof setTimeout>;

  constructor({ maxConcurrent }: { maxConcurrent: number }) {
    if (maxConcurrent < 1) throw new Error('maxConcurrent must be at least 1');
    this.maxConcurrent = maxConcurrent;
    this.currentRunning = new Set();
    this.queue = [];
  }

  private _enqueue(promiseFunction: () => Promise<R>): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      this.queue.push({ promiseFunction, resolve, reject });
      this.dequeue();
    });
  }

  enqueue(promiseFunctions: (() => Promise<R>)[], timeout?: number): Promise<R>[] {
    if (timeout) this.setPromisesTimeout(timeout);

    const promises = promiseFunctions.map((fn) => this._enqueue(fn));
    Promise.allSettled(promises).then(() => this.clearPromisesTimeout());

    return promises;
  }

  private dequeue() {
    while (this.currentRunning.size < this.maxConcurrent && this.queue.length > 0) {
      const item = this.queue.shift();
      if (!item) break;

      const { promiseFunction, resolve, reject } = item;
      this.currentRunning.add(item);

      new Promise<R>((resolve) => resolve(promiseFunction())).then(resolve, reject).finally(() => {
        this.currentRunning.delete(item);
        this.dequeue();
      });
    }
  }

  private setPromisesTimeout(ms: number) {
    this.clearPromisesTimeout();
    this.promisesTimeoutId = setTimeout(() => {
      this.clearPromisesTimeout();
      const reason = new Error('Request timeout.');

      while (this.queue.length > 0) this.queue.shift()?.reject(reason);

      for (const pending of this.currentRunning) pending.reject(reason);
      this.currentRunning.clear();
    }, ms);
  }

  private clearPromisesTimeout() {
    if (this.promisesTimeoutId) {
      clearTimeout(this.promisesTimeoutId);
      this.promisesTimeoutId = undefined;
    }
  }
}
